<script setup lang="ts">
    import type { BundleSelectorData } from '~/@types/cms';

    declare interface BundleSelector {
        bundleSelectorId: string;
    }

    const props = defineProps<BundleSelector>();

    const appStore = useAppStore();
    const nuxtApp = useNuxtApp();
    const { t, locale, isoLocale } = useLocales();
    const { queryParams } = usePreview();
    const route = useRoute();
    const { isUrlCurrentPath, resolveUrlLink } = useURL();

    const bundleSelector = appStore.getReference<BundleSelectorData>(props.bundleSelectorId);

    const { data, status } = useAsyncData<
        {
            title: string;
            description?: string;
            url: string | undefined;
            price?: string | null;
            compareAtPrice?: string | null;
            isSelected: boolean;
        }[]
    >(`bundle-selector-${props.bundleSelectorId}`, async () => {
        if (!bundleSelector) return [];
        const baseUrl = resolveUrlLink(bundleSelector.selector.base);
        if (!baseUrl.url) return [];
        const urls = [baseUrl.url];
        const data = [
            {
                // TODO: replace with text from CMS
                title: bundleSelector.selector.base_title,
                url: baseUrl.url,
                isSelected: isUrlCurrentPath(baseUrl.url),
            },
            bundleSelector.selector.bundles.sequence
                .map((id) => {
                    const bundle = bundleSelector.selector.bundles.components.find((component) => component.id === id);
                    if (!bundle) return null;
                    const bundleUrl = resolveUrlLink(bundle.linked_bundle_link);
                    if (!bundleUrl.url) return null;
                    urls.push(bundleUrl.url);
                    return {
                        title: bundle.title,
                        description: bundle.description,
                        url: bundleUrl.url,
                        isSelected: isUrlCurrentPath(bundleUrl.url),
                    };
                })
                .filter(isDefined),
        ].flat();

        const prices = await $fetch('/api/bundle-selector-price', {
            query: {
                locale: locale.value,
                isoLocale: isoLocale.value,
                urls,
                ...queryParams.value,
            },
        });
        return data.map((selector) => {
            const { price, compareAtPrice, hasDifferentPrices } = prices[selector.url] || {};
            return {
                ...selector,
                price: price && hasDifferentPrices ? t('price.priceFrom', { price }) : price,
                compareAtPrice,
            };
        });
    });

    const selectedQuery = computed(() => {
        const params = new URLSearchParams(queryParams.value as Record<string, string>);
        const variant = route.query.variant?.toString();
        if (variant) params.append('variant', variant);
        return params.toString();
    });

    const trackClick = (label: string) => {
        nuxtApp.$gtm.selectItem({
            item_list_id: 'product_toggle',
            items: [
                {
                    item_name: label,
                },
            ],
        });
    };
</script>
<template>
    <div
        v-if="bundleSelector && data?.length && status === 'success'"
        class="mol-bundle-selector w-full border-t border-woom-grey-mid">
        <atm-heading
            class="py-4"
            level="h2"
            size="xs">
            <renderer-html :content="woomTextFormat(bundleSelector.selector.headline)" />
        </atm-heading>
        <div class="flex flex-col gap-4">
            <nuxt-link
                v-for="bundle in data"
                :key="bundle.title"
                :to="`${$helpers.generateLocalePath(bundle.url)}?${selectedQuery}`"
                :aria-current="bundle.isSelected ? 'page' : undefined"
                :class="[
                    'rounded-md border-2 border-woom-black bg-woom-grey-mid bg-opacity-0 transition-colors',
                    bundle.isSelected ? 'pointer-events-none' : 'hover:bg-opacity-100',
                ]"
                @click="trackClick(bundle.title)">
                <div :class="['w-full border-b border-woom-black p-3 font-bold', { 'bg-woom-black text-white': bundle.isSelected }]">
                    <renderer-html :content="woomTextFormat(bundle.title)" />
                </div>
                <div class="p-3">
                    <div
                        v-if="bundle.description"
                        class="whitespace-pre-wrap">
                        {{ bundle.description }}
                    </div>
                    <atm-price
                        :price="bundle.price"
                        :compare-at-price="bundle.compareAtPrice"
                        alignment="right" />
                </div>
            </nuxt-link>
        </div>
    </div>
</template>
